/**
 * OR alternative expected-values helpers.
 * Reuses the existing single expectedValue field as the first/active value.
 * logicalOperator NONE keeps current assertion behavior unchanged.
 */

export const MAX_OR_EXPECTED_VALUES = 20;

export const LOGICAL_OPERATOR_NONE = 'NONE';
export const LOGICAL_OPERATOR_OR = 'OR';

export type LogicalOperator = typeof LOGICAL_OPERATOR_NONE | typeof LOGICAL_OPERATOR_OR;

export type OrEvaluationDetail = {
  expected: string;
  passed: boolean;
};

export type AssertionOrFields = {
  expectedValue?: string | null;
  logicalOperator?: string | null;
  expectedValues?: string[] | null;
  orEvaluationDetails?: OrEvaluationDetail[] | null;
};

export function normalizeLogicalOperator(value: unknown): LogicalOperator {
  const raw = String(value ?? '')
    .trim()
    .toUpperCase();
  return raw === LOGICAL_OPERATOR_OR ? LOGICAL_OPERATOR_OR : LOGICAL_OPERATOR_NONE;
}

export function isOrLogicalOperator(value: unknown): boolean {
  return normalizeLogicalOperator(value) === LOGICAL_OPERATOR_OR;
}

function asExpectedString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

/** Values used for execution. OR OFF always returns exactly one active value. */
export function resolveExpectedValues(assertion: AssertionOrFields | null | undefined): string[] {
  if (!assertion) return [''];
  if (isOrLogicalOperator(assertion.logicalOperator) && Array.isArray(assertion.expectedValues)) {
    return assertion.expectedValues.map((v) => asExpectedString(v));
  }
  return [asExpectedString(assertion.expectedValue)];
}

export function combineOrResults(results: boolean[]): boolean {
  if (!results.length) return false;
  return results.some(Boolean);
}

/** not_contains / not_equals mean none of the alternative values may match. */
export function isNoneOfAlternativeOperator(operator: unknown): boolean {
  const op = String(operator ?? '').trim().toLowerCase();
  return op === 'not_contains' || op === 'not_equals';
}

/**
 * Alternative expected values keep Equals/Contains as any-match (OR).
 * Not Contains / Not Equals must pass only when every alternative independently passes.
 */
export function combineAlternativeResults(operator: unknown, results: boolean[]): boolean {
  if (!results.length) return false;
  if (isNoneOfAlternativeOperator(operator)) {
    return results.every(Boolean);
  }
  return combineOrResults(results);
}

export function formatOperatorWithOr(operatorLabel: string, logicalOperator?: unknown): string {
  const label = (operatorLabel ?? '').trim() || 'Equals';
  return isOrLogicalOperator(logicalOperator) ? `${label} (OR)` : label;
}

export function formatOrExpectedDisplay(
  assertion: AssertionOrFields | null | undefined,
  fallbackDisplay: string
): string {
  if (!isOrLogicalOperator(assertion?.logicalOperator)) {
    return fallbackDisplay;
  }
  const values = resolveExpectedValues(assertion).map((v) => (v === '' ? 'NA' : v));
  if (values.length < 2) return fallbackDisplay;
  return values.join(' OR ');
}

export function formatOrEvaluationDetails(details: OrEvaluationDetail[] | null | undefined): string {
  if (!Array.isArray(details) || details.length === 0) return '';
  return details
    .map((d) => `${d.expected} -> ${d.passed ? 'PASS' : 'FAIL'}`)
    .join('\n');
}

export function validateOrExpectedValues(values: unknown[]): string | null {
  if (!Array.isArray(values) || values.length < 2) {
    return 'Please enter a value for each OR condition.';
  }
  if (values.length > MAX_OR_EXPECTED_VALUES) {
    return `A maximum of ${MAX_OR_EXPECTED_VALUES} alternative expected values is allowed.`;
  }
  const hasEmpty = values.some((v) => asExpectedString(v).trim() === '');
  if (hasEmpty) {
    return 'Please enter a value for each OR condition.';
  }
  return null;
}

export function payloadFromOrState(args: {
  orEnabled: boolean;
  expectedValue: unknown;
  expectedValues?: unknown[] | null;
}): {
  logicalOperator: LogicalOperator;
  expectedValue: string;
  expectedValues: string[] | undefined;
} {
  const first = asExpectedString(args.expectedValue);
  if (!args.orEnabled) {
    return {
      logicalOperator: LOGICAL_OPERATOR_NONE,
      expectedValue: first,
      expectedValues: undefined,
    };
  }
  const values = (args.expectedValues ?? []).map((v) => asExpectedString(v));
  return {
    logicalOperator: LOGICAL_OPERATOR_OR,
    expectedValue: values[0] ?? first,
    expectedValues: values,
  };
}

export function normalizeAssertionOrFields(source: Record<string, unknown> | null | undefined): {
  logicalOperator: LogicalOperator;
  expectedValues?: string[];
  orEvaluationDetails?: OrEvaluationDetail[];
} {
  const raw = source ?? {};
  const logicalOperator = normalizeLogicalOperator(
    raw.logicalOperator ?? raw.logical_operator
  );
  const rawValues = raw.expectedValues ?? raw.expected_values;
  let expectedValues: string[] | undefined;
  if (Array.isArray(rawValues)) {
    expectedValues = rawValues.map((v) => asExpectedString(v));
  }
  const rawDetails = raw.orEvaluationDetails ?? raw.or_evaluation_details;
  let orEvaluationDetails: OrEvaluationDetail[] | undefined;
  if (Array.isArray(rawDetails)) {
    orEvaluationDetails = rawDetails.map((d) => {
      const item = (d ?? {}) as Record<string, unknown>;
      return {
        expected: asExpectedString(item.expected ?? item.expectedValue),
        passed: item.passed === true,
      };
    });
  }
  return { logicalOperator, expectedValues, orEvaluationDetails };
}
